import { useEffect, useMemo, useRef, useState } from 'react';
import { api, COPY_FIELDS, type CopyField, type CopyTexts } from '@/lib/api';
import { classNames } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import { ETIQUETAS_CAMPO, IDIOMAS } from '@/components/copy/tipos';
import { AlertTriangle, Check, Languages, Loader2, RotateCcw } from 'lucide-react';

interface Props {
  abierto: boolean;
  anuncioId: number;
  idiomaOrigen: string;
  /** Textos del idioma origen. Solo se mandan los que tienen contenido. */
  campos: CopyTexts;
  idiomasDestino: string[];
  onCerrar: () => void;
  onTerminado: () => void;
}

type EstadoIdioma = 'pendiente' | 'girando' | 'hecho' | 'error';
type Fase = 'confirmar' | 'ejecutando' | 'resumen';

const CLASES_ESTADO: Record<EstadoIdioma, string> = {
  pendiente: 'bg-gray-100 text-gray-500',
  girando: 'bg-blue-100 text-blue-700',
  hecho: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
};

function nombreIdioma(codigo: string): string {
  return IDIOMAS.find((i) => i.code === codigo)?.label ?? codigo.toUpperCase();
}

function etiquetaCampo(campo: CopyField): string {
  return ETIQUETAS_CAMPO[campo] ?? campo;
}

function formatearTiempo(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function TraducirTodoFlujo({
  abierto,
  anuncioId,
  idiomaOrigen,
  campos,
  idiomasDestino,
  onCerrar,
  onTerminado,
}: Props) {
  const [fase, setFase] = useState<Fase>('confirmar');
  const [estados, setEstados] = useState<Record<string, EstadoIdioma>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [tanda, setTanda] = useState<string[]>([]);
  const [segundos, setSegundos] = useState(0);
  const [cancelado, setCancelado] = useState(false);

  /** Una tanda viva sobrevive a cerrar el modal, así que hay que saberlo fuera del render. */
  const enCursoRef = useRef(false);
  const vivoRef = useRef(true);

  useEffect(() => () => { vivoRef.current = false; }, []);

  const camposConTexto = useMemo(
    () =>
      COPY_FIELDS
        .map((campo) => [campo, (campos[campo] ?? '').trim()] as const)
        .filter(([, texto]) => texto.length > 0),
    [campos],
  );

  useEffect(() => {
    // Reabrir con una tanda todavía viva no la reinicia: el usuario vuelve a ver
    // el progreso de verdad en vez de una pantalla de confirmación que mentiría.
    if (!abierto || enCursoRef.current) return;
    setFase('confirmar');
    setEstados({});
    setErrores({});
    setTanda([]);
    setSegundos(0);
    setCancelado(false);
  }, [abierto]);

  useEffect(() => {
    if (fase !== 'ejecutando') return;
    const id = window.setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [fase]);

  const resueltosTanda = tanda.filter(
    (l) => estados[l] === 'hecho' || estados[l] === 'error',
  ).length;
  const porcentaje = tanda.length === 0 ? 0 : Math.round((resueltosTanda / tanda.length) * 100);
  const restante =
    resueltosTanda > 0 && resueltosTanda < tanda.length
      ? Math.ceil((segundos / resueltosTanda) * (tanda.length - resueltosTanda))
      : null;

  const guardados = idiomasDestino.filter((l) => estados[l] === 'hecho');
  const fallidos = idiomasDestino.filter((l) => estados[l] === 'error');

  const nadaQueTraducir = camposConTexto.length === 0 || idiomasDestino.length === 0;

  async function lanzar(idiomas: string[]) {
    if (idiomas.length === 0) return;

    const fields: Record<string, string> = {};
    camposConTexto.forEach(([campo, texto]) => { fields[campo] = texto; });

    enCursoRef.current = true;
    setTanda(idiomas);
    setSegundos(0);
    setCancelado(false);
    // Solo se reinician los idiomas de esta tanda: en un reintento los que ya
    // entraron siguen contando como guardados en el resumen final.
    setEstados((prev) => {
      const siguiente = { ...prev };
      idiomas.forEach((l) => { siguiente[l] = 'pendiente'; });
      return siguiente;
    });
    setErrores((prev) => {
      const siguiente = { ...prev };
      idiomas.forEach((l) => { delete siguiente[l]; });
      return siguiente;
    });
    setFase('ejecutando');

    const marcar = (lang: string, estado: EstadoIdioma) => {
      if (!vivoRef.current) return;
      setEstados((prev) => ({ ...prev, [lang]: estado }));
    };
    const anotarError = (lang: string, motivo: string) => {
      if (!vivoRef.current) return;
      setErrores((prev) => ({ ...prev, [lang]: motivo }));
    };

    // Una llamada por idioma, todas a la vez: el servidor traduce y guarda cada
    // idioma por su cuenta, así que un fallo no arrastra a los demás.
    await Promise.all(
      idiomas.map(async (lang) => {
        marcar(lang, 'girando');
        try {
          const res = await api.translateCopyAllFields(anuncioId, {
            source_lang: idiomaOrigen,
            fields,
            target_langs: [lang],
          });
          // `saved` es la única prueba de que Hostify aceptó el idioma: la
          // respuesta puede traer el texto traducido y aun así haber fallado al
          // escribirlo.
          if (res.saved?.includes(lang)) {
            marcar(lang, 'hecho');
          } else {
            marcar(lang, 'error');
            anotarError(
              lang,
              res.errors?.find((e) => e.lang === lang)?.error
                || 'El servidor no confirmó que se guardara.',
            );
          }
        } catch (err) {
          marcar(lang, 'error');
          anotarError(lang, err instanceof Error ? err.message : 'Error de conexión');
        }
      }),
    );

    enCursoRef.current = false;
    if (!vivoRef.current) return;
    setFase('resumen');
    onTerminado();
  }

  const cancelar = () => setCancelado(true);

  const ejecutando = fase === 'ejecutando';

  const cerrarDesdeCabecera = () => {
    // El Modal de la casa siempre pinta la X y el backdrop cierra al pulsarlo.
    // Durante la tanda no puede cerrar (se perdería el progreso de vista), así
    // que la tratamos como el botón Cancelar en lugar de dejarla muerta.
    if (ejecutando && !cancelado) cancelar();
    else onCerrar();
  };

  const insigniaIdioma = (lang: string) => {
    const estado = estados[lang] ?? 'pendiente';
    return (
      <span
        key={lang}
        title={errores[lang] || nombreIdioma(lang)}
        className={classNames('badge gap-1', CLASES_ESTADO[estado])}
      >
        {estado === 'girando' && <Loader2 size={12} className="animate-spin" />}
        {estado === 'hecho' && <Check size={12} />}
        {estado === 'error' && <AlertTriangle size={12} />}
        {lang.toUpperCase()}
      </span>
    );
  };

  return (
    <Modal
      isOpen={abierto}
      onClose={cerrarDesdeCabecera}
      title="Traducir todos los campos"
      size="lg"
    >
      {fase === 'confirmar' && (
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <Languages size={20} className="text-sereno-dark/40 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-sereno-dark/70">
              Se traducirán <strong>{camposConTexto.length}</strong>{' '}
              {camposConTexto.length === 1 ? 'campo con texto' : 'campos con texto'} desde{' '}
              <strong>{nombreIdioma(idiomaOrigen)}</strong> a{' '}
              <strong>{idiomasDestino.length}</strong>{' '}
              {idiomasDestino.length === 1 ? 'idioma' : 'idiomas'}.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-700">
              <strong>Esto guarda directamente en Hostify.</strong> El servidor traduce y
              escribe cada idioma él mismo: no hay una pantalla previa para revisar los
              textos y lo que ya hubiera en esos idiomas se sobrescribe.
            </p>
          </div>

          <div>
            <p className="label">Idiomas de destino</p>
            {idiomasDestino.length === 0 ? (
              <p className="text-sm text-rose-600">
                No hay ningún idioma de destino seleccionado.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {idiomasDestino.map((lang) => (
                  <span
                    key={lang}
                    title={nombreIdioma(lang)}
                    className="badge bg-sereno-blue text-sereno-dark"
                  >
                    {lang.toUpperCase()}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="label">Campos que se envían</p>
            {camposConTexto.length === 0 ? (
              <p className="text-sm text-rose-600">
                No hay ningún campo con texto en {nombreIdioma(idiomaOrigen)}. Escribe o
                genera los textos antes de traducir.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {camposConTexto.map(([campo]) => (
                  <span key={campo} className="badge bg-gray-100 text-gray-600">
                    {etiquetaCampo(campo)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onCerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              disabled={nadaQueTraducir}
              onClick={() => { void lanzar(idiomasDestino); }}
            >
              <Languages size={16} />
              Traducir y guardar
            </button>
          </div>
        </div>
      )}

      {ejecutando && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-sereno-dark">
              {resueltosTanda} / {tanda.length} idiomas procesados
            </span>
            <span className="text-xs text-sereno-dark/50 tabular-nums text-right">
              {formatearTiempo(segundos)}
              {restante !== null && (
                <>
                  <br />
                  ~{formatearTiempo(restante)} restantes
                </>
              )}
            </span>
          </div>

          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${porcentaje}%` }}
            />
          </div>

          <div className="flex flex-wrap gap-1.5">{tanda.map(insigniaIdioma)}</div>

          {cancelado ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-700">
                Cancelado. Las traducciones que ya se habían enviado siguen su curso en el
                servidor y pueden guardarse igualmente: al terminar verás el resultado real
                en la lista de idiomas.
              </p>
            </div>
          ) : (
            <p className="text-xs text-sereno-dark/50">
              Los {tanda.length} idiomas van en paralelo. Puedes cerrar esta ventana: la
              tanda no se detiene.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {cancelado ? (
              <button type="button" className="btn-ghost" onClick={onCerrar}>
                Cerrar
              </button>
            ) : (
              <button type="button" className="btn-ghost" onClick={cancelar}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {fase === 'resumen' && (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            {fallidos.length === 0 ? (
              <Check size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={20} className="text-yellow-700 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-sm text-sereno-dark/70">
              {guardados.length === 0
                ? 'No se guardó ningún idioma.'
                : `Guardados en Hostify ${guardados.length} ${guardados.length === 1 ? 'idioma' : 'idiomas'}.`}
              {fallidos.length > 0 && ` ${fallidos.length} con errores.`}
            </p>
          </div>

          {guardados.length > 0 && (
            <div>
              <p className="label">Guardados</p>
              <div className="flex flex-wrap gap-1.5">
                {guardados.map((lang) => (
                  <span
                    key={lang}
                    title={nombreIdioma(lang)}
                    className="badge bg-green-100 text-green-700 gap-1"
                  >
                    <Check size={12} />
                    {lang.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {fallidos.length > 0 && (
            <div>
              <p className="label">Errores</p>
              <ul className="space-y-1">
                {fallidos.map((lang) => (
                  <li key={lang} className="text-sm text-rose-600">
                    <span className="font-medium">{lang.toUpperCase()}</span>
                    {' — '}
                    {errores[lang] || 'Error desconocido'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {fallidos.length > 0 && (
              <button
                type="button"
                className="btn-secondary flex items-center gap-2"
                onClick={() => { void lanzar(fallidos); }}
              >
                <RotateCcw size={16} />
                Reintentar los que fallaron
              </button>
            )}
            <button type="button" className="btn-primary" onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
